import { useState, useEffect, useRef, useCallback } from "react";
import { trackCarousel } from "../../analytics/tracking";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons";

export default function ImageCarousel({ images }) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoPlayRef = useRef(null);

  const total = images?.length || 0;

  const goTo = useCallback(
    (index) => {
      if (isTransitioning || total === 0) return;
      setIsTransitioning(true);
      setCurrent((index + total) % total);
      setTimeout(() => setIsTransitioning(false), 400);
    },
    [isTransitioning, total]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 4000);
    return () => clearInterval(autoPlayRef.current);
  }, [total]);

  const pauseAutoPlay = () => {
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 4000);
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      pauseAutoPlay();
      if (diff > 0) {
        next();
        trackCarousel("swipe", current + 1);
      } else {
        prev();
        trackCarousel("swipe", current - 1);
      }
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="relative select-none">
      <div
        className="relative overflow-hidden aspect-[9/19.5] max-w-[320px] mx-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex h-full"
          style={{
            width: `${total * 100}%`,
            transform: `translateX(-${(current * 100) / total}%)`,
            transition: "transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)",
          }}
        >
          {images.map((img, i) => (
            <div key={i} className="h-full" style={{ width: `${100 / total}%` }}>
              <img src={img} alt={`Screenshot ${i + 1}`} loading="lazy" className="w-full h-full object-cover" draggable={false} />
            </div>
          ))}
        </div>

        <div className="absolute inset-y-0 left-0 w-12 pointer-events-none" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.15), transparent)" }} />
        <div className="absolute inset-y-0 right-0 w-12 pointer-events-none" style={{ background: "linear-gradient(to left, rgba(0,0,0,0.15), transparent)" }} />

        <button
          onClick={() => {
            pauseAutoPlay();
            prev();
            trackCarousel("arrow", current - 1);
          }}
          className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/30 transition-colors"
          style={{ backdropFilter: "blur(4px)", background: "rgba(255,255,255,0.15)" }}
          aria-label="Previous"
        >
          <ChevronLeftIcon />
        </button>
        <button
          onClick={() => {
            pauseAutoPlay();
            next();
            trackCarousel("arrow", current + 1);
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/30 transition-colors"
          style={{ backdropFilter: "blur(4px)", background: "rgba(255,255,255,0.15)" }}
          aria-label="Next"
        >
          <ChevronRightIcon />
        </button>

        <div
          className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-white text-xs font-medium"
          style={{ backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.35)" }}
        >
          {current + 1} / {total}
        </div>
      </div>

      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10 px-3 py-1 rounded-full"
        style={{ background: "rgba(0,0,0,0)", backdropFilter: "blur(4px)" }}
      >
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              pauseAutoPlay();
              goTo(i);
              trackCarousel("dot", i);
            }}
            aria-label={`Go to slide ${i + 1}`}
            className="transition-all duration-300"
            style={{
              width: current === i ? "28px" : "8px",
              height: "8px",
              borderRadius: "999px",
              background: current === i ? "#2563eb" : "gray",
            }}
          />
        ))}
      </div>
    </div>
  );
}